import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Card, Empty, Input, Pagination, Spin, Switch, Tag, Typography, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { fetchAdminUsers, updateUserAdminStatus, type AdminUserRow } from '../../services';
import { useAppSelector } from '../../hooks/redux';
import './AdminUsers.scss';

const { Text, Title } = Typography;

const AdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.user);
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 12;

  const loadUsers = useCallback(async (nextPage: number, nextSearch: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchAdminUsers({
        take: pageSize,
        skip: (nextPage - 1) * pageSize,
        search: nextSearch,
      });
      setUsers(response.users || []);
      setTotal(response.usersCount || 0);
    } catch (requestError: any) {
      const nextError = requestError?.response?.errors?.[0]?.message || requestError?.message || 'Unable to load users';
      setError(nextError);
      message.error(nextError);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (!user.isAdmin) {
      navigate('/UserPage');
      return;
    }

    void loadUsers(1, '');
  }, [loadUsers, navigate, user]);

  const handleSearch = (value = search) => {
    const nextSearch = value.trim();

    setActiveSearch(nextSearch);
    setPage(1);
    void loadUsers(1, nextSearch);
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    void loadUsers(nextPage, activeSearch);
  };

  const handleAdminChange = async (targetUser: AdminUserRow, isAdmin: boolean) => {
    if (targetUser.id === user?.id && !isAdmin) {
      message.warning('You cannot remove your own admin access.');
      return;
    }

    setSavingUserId(targetUser.id);

    try {
      const response = await updateUserAdminStatus(targetUser.id, isAdmin);
      const updatedUser = response.updateUser;

      setUsers((prev) =>
        prev.map((item) => (item.id === updatedUser.id ? { ...item, ...updatedUser } : item))
      );
      message.success(isAdmin ? 'Admin access granted.' : 'Admin access removed.');
    } catch (requestError: any) {
      message.error(requestError?.response?.errors?.[0]?.message || requestError?.message || 'Unable to update admin access.');
    } finally {
      setSavingUserId(null);
    }
  };

  if (!user?.isAdmin) {
    return (
      <div className="admin-users">
        <Title level={3}>Access denied</Title>
        <Text>This section is reserved for administrators.</Text>
      </div>
    );
  }

  return (
    <div className="admin-users">
      <div className="admin-users__header">
        <div>
          <Title level={3}>Admin users</Title>
          <Text type="secondary">Grant or remove administrator access for existing users.</Text>
        </div>
        <Input.Search
          allowClear
          className="admin-users__search"
          placeholder="Search users"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            if (!event.target.value) {
              setActiveSearch('');
              setPage(1);
              void loadUsers(1, '');
            }
          }}
          onSearch={handleSearch}
        />
      </div>

      {error && <Alert type="error" showIcon message="Unable to load users" description={error} />}

      {loading ? (
        <div className="admin-users__loading">
          <Spin size="large" />
        </div>
      ) : users.length === 0 ? (
        <Empty description="No users found" />
      ) : (
        <div className="admin-users__list">
          {users.map((item) => {
            const isCurrentUser = item.id === user.id;

            return (
              <Card key={item.id} className="admin-users__card">
                <div className="admin-users__identity">
                  <Text strong>{item.name}</Text>
                  <Text type="secondary" className="admin-users__email">{item.email}</Text>
                </div>
                <div className="admin-users__controls">
                  {item.isAdmin ? <Tag color="blue">Admin</Tag> : <Tag>User</Tag>}
                  {isCurrentUser && <Tag color="gold">You</Tag>}
                  <Switch
                    checked={Boolean(item.isAdmin)}
                    checkedChildren="Admin"
                    unCheckedChildren="User"
                    loading={savingUserId === item.id}
                    disabled={isCurrentUser}
                    onChange={(checked) => handleAdminChange(item, checked)}
                  />
                </div>
              </Card>
            );
          })}
          {total > pageSize && (
            <div className="admin-users__pagination">
              <Pagination
                current={page}
                pageSize={pageSize}
                total={total}
                showSizeChanger={false}
                onChange={handlePageChange}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
